<template>
  <div class="admin-dashboard">
    <!-- Sidebar Navigation -->
    <div class="sidebar">
      <div class="sidebar-header">
        <i class="fas fa-graduation-cap" style="color: var(--primary-blue); font-size: 24px; margin-right: 10px;"></i>
        <h3>Wrona School</h3>
      </div>
      <div class="sidebar-content">
        <!-- Navigation Links -->
        <div class="nav-links">
          <router-link to="/admin" class="nav-link">
            <i class="fas fa-tachometer-alt"></i>
            <span>{{ $t('adminDashboard.dashboard') }}</span>
          </router-link>
          <router-link v-if="role === 'admin' && !viewAsTeacher" to="/admin/user-management" class="nav-link">
            <i class="fas fa-users"></i>
            <span>{{ $t('adminDashboard.manageUsers') }}</span>
          </router-link>
          <router-link v-if="role === 'admin' && !viewAsTeacher" to="/admin/transactions-management" class="nav-link">
            <i class="fas fa-money-bill-wave"></i>
            <span>{{ $t('adminDashboard.transactionsManagement') }}</span>
          </router-link>
          <router-link v-if="role === 'admin' || role === 'teacher' || role === 'senior_teacher' || viewAsTeacher" to="/admin/schedules" class="nav-link">
            <i class="fas fa-calendar-alt"></i>
            <span>{{ $t('adminDashboard.manageSchedules') }}</span>
          </router-link>
          <router-link v-if="role === 'admin' || role === 'teacher' || role === 'senior_teacher'|| viewAsTeacher" to="/admin/account" class="nav-link">
            <i class="fas fa-user-circle"></i>
            <span>{{ $t('adminDashboard.account') }}</span>
          </router-link>
          <router-link v-if="role === 'admin' && !viewAsTeacher" to="/admin/groups-management" class="nav-link">
            <i class="fas fa-user-friends"></i>
            <span>{{ $t('adminDashboard.groupsManagement') }}</span>
          </router-link>
          <router-link v-if="role === 'admin' && !viewAsTeacher" to="/admin/lessons-management" class="nav-link">
            <i class="fas fa-book"></i>
            <span>{{ $t('adminDashboard.lessonsManagement') }}</span>
          </router-link>
          <router-link v-if="isAdmin" to="/admin/billing-plans" class="nav-link">
            <i class="fas fa-file-invoice-dollar"></i> {{ $t('adminDashboard.billingPlans') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="main-content">
      <!-- Top Header -->
      <div class="top-header">
        <div class="header-title">
          <h2>{{ $t('adminDashboard.title') }}</h2>
        </div>
        <div class="header-controls">
          <!-- Teacher View Toggle -->
          <button v-if="isAdmin" @click="toggleView" class="btn-toggle">
            <i class="fas" :class="viewAsTeacher ? 'fa-user-shield' : 'fa-chalkboard-teacher'"></i>
            {{ viewAsTeacher ? $t('adminDashboard.viewAsAdmin') : $t('adminDashboard.viewAsTeacher') }}
          </button>
          
          <!-- Language Dropdown -->
          <div class="dropdown" ref="languageDropdown">
            <button type="button" class="btn-dropdown" @click.stop="toggleLangDropdown">
              <i class="fas fa-globe"></i>
              <span>{{ currentLanguage }}</span>
              <i class="fas fa-chevron-down"></i>
            </button>
            <div class="dropdown-menu" :class="{ show: isLangDropdownOpen }">
              <a class="dropdown-item" href="#" @click.prevent.stop="changeLanguage('en')">
                <i class="fas fa-flag-usa"></i> English
              </a>
              <a class="dropdown-item" href="#" @click.prevent.stop="changeLanguage('uk')">
                <i class="fas fa-flag"></i> Українська
              </a>
              <a class="dropdown-item" href="#" @click.prevent.stop="changeLanguage('pl')">
                <i class="fas fa-flag"></i> Polski
              </a>
            </div>
          </div>
          
          <!-- Logout Button -->
          <button @click="logout" class="btn-logout">
            <i class="fas fa-sign-out-alt"></i>
            <span>{{ $t('adminDashboard.logout') }}</span>
          </button>
        </div>
      </div>

      <!-- Teacher Selection (when in teacher view) -->
      <div v-if="viewAsTeacher" class="teacher-selector">
        <div class="input-group">
          <span class="input-label">{{ $t('adminDashboard.selectTeacher') }}</span>
          <div class="input-wrapper">
            <input 
              type="text" 
              v-model="teacherName" 
              @input="fetchTeachers" 
              :placeholder="$t('adminDashboard.selectTeacher')" 
              class="form-input"
            />
            <div v-if="teacherSuggestions.length > 0" class="dropdown-suggestions">
              <ul>
                <li v-for="teacher in teacherSuggestions" :key="teacher.id" @click="selectTeacher(teacher)">
                  {{ teacher.name }}
                </li>
              </ul>
            </div>
            <div v-if="teacherError" class="error-message">
              {{ $t('adminDashboard.noTeacherFound') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Router View - Content of selected route -->
      <div class="content-area">
        <div v-if="$route.path === '/admin'" class="dashboard-welcome">
          <!-- Admin Dashboard -->
          <div v-if="role === 'admin' && !viewAsTeacher">
            <div class="welcome-header">
              <h3>{{ $t('adminDashboard.welcomeHeader') }}</h3>
              <p>{{ $t('adminDashboard.welcomeSubheader') }}</p>
            </div>
            
            <!-- Admin Statistics -->
            <div class="quick-stats">
              <div class="stat-card">
                <i class="fas fa-users stat-icon"></i>
                <div class="stat-content">
                  <h4>{{ $t('adminDashboard.students') }}</h4>
                  <p class="stat-value">{{ stats.students || '0' }}</p>
                </div>
              </div>
              
              <div class="stat-card">
                <i class="fas fa-chalkboard-teacher stat-icon"></i>
                <div class="stat-content">
                  <h4>{{ $t('adminDashboard.teachers') }}</h4>
                  <p class="stat-value">{{ stats.teachers || '0' }}</p>
                </div>
              </div>
              
              <div class="stat-card">
                <i class="fas fa-book-open stat-icon"></i>
                <div class="stat-content">
                  <h4>{{ $t('adminDashboard.lessons') }}</h4>
                  <p class="stat-value">{{ stats.lessons || '0' }}</p>
                </div>
              </div>
              
              <div class="stat-card">
                <i class="fas fa-user-friends stat-icon"></i>
                <div class="stat-content">
                  <h4>{{ $t('adminDashboard.groups') }}</h4>
                  <p class="stat-value">{{ stats.groups || '0' }}</p>
                </div>
              </div>
            </div>
            
            <!-- Financial Analytics -->
            <div class="finance-analytics">
              <!-- Students in Debt Section -->
              <div class="analytics-section students-debt">
                <h3>{{ $t('adminDashboard.studentsInDebt') }}</h3>
                
                <div v-if="loadingDebts" class="loading-indicator">
                  <i class="fas fa-spinner fa-spin"></i> {{ $t('adminDashboard.loading') }}
                </div>
                
                <div v-else-if="studentsInDebt.length === 0" class="empty-state">
                  <i class="fas fa-check-circle"></i>
                  <p>{{ $t('adminDashboard.noStudentsInDebt') }}</p>
                </div>
                
                <div v-else class="debt-list">
                  <div v-for="student in studentsInDebt" :key="student.id" class="debt-item">
                    <div class="student-info">
                      <div class="student-name">{{ student.first_name }} {{ student.last_name }}</div>
                      <div class="student-balance negative">{{ student.balance }} UAH</div>
                    </div>
                    <div class="debt-actions">
                      <button class="btn-action" @click="$router.push(`/admin/transactions-management?userId=${student.id}&action=add`)">
                        <i class="fas fa-money-bill"></i> {{ $t('adminDashboard.recordPayment') }}
                      </button>
                      <button class="btn-action secondary" @click="$router.push(`/admin/user-management?userId=${student.id}`)">
                        <i class="fas fa-user"></i> {{ $t('adminDashboard.viewProfile') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Finance Graph Section -->
              <div class="analytics-section finance-graph">
                <h3>{{ $t('adminDashboard.financialOverview') }}</h3>
                
                <div class="graph-controls">
                  <div class="date-range-selector">
                    <button 
                      v-for="range in timeRanges" 
                      :key="range.value" 
                      @click="setTimeRange(range.value)"
                      :class="['range-button', { active: currentTimeRange === range.value }]"
                    >
                      {{ range.label }}
                    </button>
                  </div>
                  
                  <!-- Add navigation controls -->
                  <div class="period-navigation">
                    <button class="nav-button" @click="navigatePeriod(-1)">
                      <i class="fas fa-chevron-left"></i> {{ $t('adminDashboard.previous') }}
                    </button>
                    <span class="current-period">{{ currentPeriodLabel }}</span>
                    <button class="nav-button" @click="navigatePeriod(1)" :disabled="isCurrentPeriod">
                      <i class="fas fa-chevron-right"></i> {{ $t('adminDashboard.next') }}
                    </button>
                    <button class="today-button" @click="resetPeriod" :disabled="isCurrentPeriod">
                      {{ $t('adminDashboard.current') }}
                    </button>
                  </div>
                </div>
                
                <div class="graph-container">
                  <div v-if="isLoadingChart" class="loading-chart">
                    <i class="fas fa-spinner fa-spin"></i> {{ $t('adminDashboard.loading') }}
                  </div>
                  <!-- The canvas will be created dynamically -->
                </div>
                
                <div class="graph-summary">
                  <div class="summary-item income">
                    <div class="summary-label">{{ $t('adminDashboard.totalIncome') }}</div>
                    <div class="summary-value">{{ financeSummary.income }} UAH</div>
                  </div>
                  <div class="summary-item expenses">
                    <div class="summary-label">{{ $t('adminDashboard.totalExpenses') }}</div>
                    <div class="summary-value">{{ financeSummary.expenses }} UAH</div>
                  </div>
                  <div class="summary-item profits">
                    <div class="summary-label">{{ $t('adminDashboard.totalProfits') }}</div>
                    <div class="summary-value">{{ financeSummary.totalProfits }} UAH</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Teacher Dashboard -->
          <div v-else>
            <div class="welcome-header">
              <h3>{{ $t('teacherDashboard.welcomeHeader') }}</h3>
              <p>{{ $t('teacherDashboard.welcomeSubheader') }}</p>
            </div>
            
            <!-- Teacher Statistics -->
            <div class="teacher-stats">
              <div class="stat-card">
                <i class="fas fa-book-open stat-icon"></i>
                <div class="stat-content">
                  <h4>{{ $t('teacherDashboard.totalLessons') }}</h4>
                  <p class="stat-value">{{ teacherStats.totalLessons || '0' }}</p>
                </div>
              </div>
              
              <div class="stat-card">
                <i class="fas fa-calendar-alt stat-icon"></i>
                <div class="stat-content">
                  <h4>{{ $t('teacherDashboard.upcomingLessons') }}</h4>
                  <p class="stat-value">{{ teacherStats.upcomingLessons || '0' }}</p>
                </div>
              </div>
              
              <div class="stat-card balance-card">
                <i class="fas fa-wallet stat-icon"></i>
                <div class="stat-content">
                  <h4>{{ $t('teacherDashboard.currentBalance') }}</h4>
                  <p class="stat-value">{{ teacherStats.currentBalance || '0' }} UAH</p>
                </div>
              </div>
            </div>
            
            <!-- Teacher Schedule -->
            <div class="upcoming-schedule">
              <h3>{{ $t('teacherDashboard.upcomingSchedule') }}</h3>
              
              <div v-if="teacherStats.nextLessons && teacherStats.nextLessons.length > 0" class="schedule-list">
                <div v-for="lesson in teacherStats.nextLessons" :key="lesson.id" class="schedule-item">
                  <div class="schedule-date">
                    <span class="schedule-day">{{ formatDay(lesson.date) }}</span>
                    <span class="schedule-date-num">{{ formatDate(lesson.date) }}</span>
                  </div>
                  
                  <div class="schedule-details">
                    <div class="schedule-time">{{ formatTime(lesson.time_from) }} - {{ formatTime(lesson.time_to) }}</div>
                    <div class="schedule-group">{{ lesson.group_name || lesson.student_name }}</div>
                  </div>
                </div>
              </div>
              
              <div v-else class="no-lessons">
                <i class="far fa-calendar-times" style="font-size: 3rem; color: #ccc; margin-bottom: 10px;"></i>
                <p>{{ $t('teacherDashboard.noUpcomingLessons') }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <router-view :viewAsTeacher="viewAsTeacher" :selectedTeacher="selectedTeacher">
        </router-view>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '../router/axiosSetup';
import { mapState } from 'vuex';

export default {
  data() {
    return {
      role: localStorage.getItem('role'), // Get the user's role from localStorage
      viewAsTeacher: false, // State to toggle between admin and teacher view
      teachers: [], // List of teachers
      selectedTeacher: '', // Selected teacher ID
      teacherName: '', // Input for teacher's name
      teacherSuggestions: [], // Suggestions for teacher's name
      teacherError: false, // Error state for teacher suggestions
      currentLanguage: 'English', // Default language
      stats: {
        students: null,
        teachers: null,
        lessons: null,
        groups: null
      },
      teacherStats: {
        totalLessons: 0,
        upcomingLessons: 0,
        currentBalance: 0,
        nextLessons: []
      },
      studentsInDebt: [],
      loadingDebts: true,
      financeChart: null,
      currentTimeRange: 'month',
      timeRanges: [
        { value: 'week', label: this.$t('adminDashboard.week') },
        { value: 'month', label: this.$t('adminDashboard.month') },
        { value: 'quarter', label: this.$t('adminDashboard.quarter') },
        { value: 'year', label: this.$t('adminDashboard.year') }
      ],
      financeSummary: {
        income: 0,
        expenses: 0,
        totalProfits: 0
      },
      isLoadingChart: false,
      chartRenderTimeout: null,
      periodOffset: 0,
      isCurrentPeriod: true,
      isLangDropdownOpen: false
    };
  },
  watch: {
    '$route.path': function(newPath) {
      if (newPath === '/admin') {
        this.fetchDashboardStats();
      }
      if (newPath === '/admin' && this.role === 'admin' && !this.viewAsTeacher) {
        this.fetchStudentsInDebt();
        this.fetchFinanceData();
      }
    },
    
    viewAsTeacher() {
      if (this.$route.path === '/admin') {
        this.fetchDashboardStats();
      }
    },
    
    selectedTeacher() {
      if (this.$route.path === '/admin' && this.viewAsTeacher) {
        this.fetchDashboardStats();
      }
    }
  },
  computed: {
    isAdmin() {
      return localStorage.getItem('role') === 'admin';
    },
    ...mapState(['language']),
    currentPeriodLabel() {
      if (this.currentTimeRange === 'week') {
      // Get the current date
      const now = new Date();
      
      // Clone the date object for timezone safety
      const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Calculate Monday of the current week (0 = Sunday, 1 = Monday, etc.)
      const dayOfWeek = currentDate.getDay();
      const diff = currentDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      
      // Get the Monday that starts the week
      const startOfWeek = new Date(currentDate.setDate(diff));
      
      // Apply the period offset
      startOfWeek.setDate(startOfWeek.getDate() + (this.periodOffset * 7));
      
      // Calculate the end of the week (Sunday)
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      // Format using the current locale
      const formatDate = (date) => {
        return date.toLocaleDateString(this.getCurrentLocale(), { 
          month: 'short', 
          day: 'numeric' 
        });
      };
      
      return `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}`;
    }
      
      if (this.currentTimeRange === 'month') {
        const now = new Date();
        const targetMonth = new Date(now.getFullYear(), now.getMonth() + this.periodOffset, 1);
        return targetMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      }
      
      if (this.currentTimeRange === 'quarter') {
        const now = new Date();
        const targetDate = new Date(now);
        targetDate.setMonth(now.getMonth() + (this.periodOffset * 3));
        const quarter = Math.floor(targetDate.getMonth() / 3) + 1;
        return `Q${quarter} ${targetDate.getFullYear()}`;
      }
      
      if (this.currentTimeRange === 'year') {
        const now = new Date();
        return `${now.getFullYear() + this.periodOffset}`;
      }
      
      return '';
    }
   
  },
  methods: {
    toggleLangDropdown() {
      this.isLangDropdownOpen = !this.isLangDropdownOpen;
      
      if (this.isLangDropdownOpen) {
        // Add click outside listener
        this.$nextTick(() => {
          document.addEventListener('click', this.closeLangDropdown);
        });
      }
    },
    
    closeLangDropdown(event) {
      // Check if the click is outside the dropdown
      if (!this.$refs.languageDropdown || !this.$refs.languageDropdown.contains(event.target)) {
        this.isLangDropdownOpen = false;
        document.removeEventListener('click', this.closeLangDropdown);
      }
    },
    async fetchStudentsInDebt() {
      try {
        this.loadingDebts = true;
        const response = await axiosInstance.get('/admin/students-in-debt', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.studentsInDebt = response.data;
      } catch (error) {
        console.error('Error fetching students in debt:', error);
      } finally {
        this.loadingDebts = false;
      }
    },

    async fetchFinanceData() {
      try {
        this.isLoadingChart = true;
        const response = await axiosInstance.get('/admin/finance-chart', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { 
            timeRange: this.currentTimeRange,
            periodOffset: this.periodOffset
          }
        });
        
        this._renderChart(response.data);
        this.calculateFinanceSummary(response.data);
        this.isLoadingChart = false;
      } catch (error) {
        console.error('Error fetching finance data:', error);
        this.isLoadingChart = false;
      }
    },

    navigatePeriod(direction) {
      this.periodOffset += direction;
      this.isCurrentPeriod = this.periodOffset === 0;
      this.fetchFinanceData(); 
    },
    
    resetPeriod() {
      this.periodOffset = 0;
      this.isCurrentPeriod = true;
      this.fetchFinanceData();
    },
    
    setTimeRange(range) {
      this.currentTimeRange = range;
      this.periodOffset = 0; // Reset offset when changing time range
      this.isCurrentPeriod = true;
      this.fetchFinanceData();
    },

    renderFinanceChart(data) {
      // First, add debouncing to prevent too-rapid updates
      if (this.chartRenderTimeout) {
        clearTimeout(this.chartRenderTimeout);
      }
      
      this.chartRenderTimeout = setTimeout(() => {
        this._renderChart(data);
      }, 300); // Delay chart rendering slightly to avoid rapid consecutive renders
    },

    _renderChart(data) {
      // Create a completely new canvas element to avoid context issues
      const container = document.querySelector('.graph-container');
      
      // Clean up any existing chart
      if (this.financeChart) {
        this.financeChart.destroy();
        this.financeChart = null;
      }
      
      // Remove old canvas
      const oldCanvas = document.getElementById('financeChart');
      if (oldCanvas) {
        oldCanvas.remove();
      }
      
      // Create new canvas
      const canvas = document.createElement('canvas');
      canvas.id = 'financeChart';
      canvas.style.width = '100%';
      canvas.style.height = '300px';
      canvas.style.backgroundColor = 'white';
      
      // Add to container
      container.appendChild(canvas);
      
      // Ensure canvas is added to DOM before continuing
      this.$nextTick(() => {
        try {
          // Now create the chart on the fresh canvas
          import('chart.js').then(module => {
            const { Chart, registerables } = module;
            Chart.register(...registerables);
            
            // Use simpler configuration to avoid issues
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              console.error('Could not get 2d context from canvas');
              return;
            }
            
            this.financeChart = new Chart(ctx, {
              type: 'bar', // Switch to bar chart which is more stable
              data: {
                labels: data.labels,
                datasets: [
                  {
                    label: this.$t('adminDashboard.income'),
                    data: data.income,
                    backgroundColor: '#2ecc71',
                    borderWidth: 0
                  },
                  {
                    label: this.$t('adminDashboard.expenses'),
                    data: data.expenses,
                    backgroundColor: '#e74c3c',
                    borderWidth: 0
                  },
                  {
                    label: this.$t('adminDashboard.profit'),
                    data: data.profit,
                    backgroundColor: '#3498db', // Blue for profit
                    type: 'line'  // Display as a line to overlay on bars
                  }
                ]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false, // Disable animations to avoid rendering issues
              }
            });
            
            console.log('Chart created with fresh canvas');
          }).catch(err => {
            console.error('Chart.js import error:', err);
          });
        } catch (error) {
          console.error('Chart rendering error:', error);
        }
      });
    },

    calculateFinanceSummary(data) {
      const income = data.income.reduce((sum, value) => sum + value, 0);
      const expenses = data.expenses.reduce((sum, value) => sum + value, 0);
      const totalProfits = data.profit.reduce((sum, value) => sum + value, 0);
      
      this.financeSummary = {
        income: income.toFixed(2),
        expenses: expenses.toFixed(2),
        totalProfits: totalProfits.toFixed(2),
      };
    },
    async fetchDashboardStats() {
      try {
        if (this.role === 'admin' && !this.viewAsTeacher) {
          // Fetch admin stats
          const response = await axiosInstance.get('/admin/dashboard-stats', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          this.stats = response.data;
        } else {
          // Fetch teacher stats
          const teacherId = this.viewAsTeacher ? this.selectedTeacher : localStorage.getItem('userId');
          const response = await axiosInstance.get(`/admin/dashboard-stats/${teacherId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          
          // Transform lesson data to match expected property names
          if (response.data && response.data.nextLessons) {
            response.data.nextLessons = response.data.nextLessons.map(lesson => ({
              id: lesson.id,
              date: lesson.lesson_date,        // Map lesson_date to date
              time_from: lesson.start_time,    // Map start_time to time_from
              time_to: lesson.end_time,        // Map end_time to time_to
              group_name: lesson.group_name,
              student_name: lesson.student_name
            }));
          }
          
          this.teacherStats = response.data;
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      }
    },
    formatDay(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      
      // Use the current app language instead of hard-coded 'en-US'
      const locale = this.getCurrentLocale();
      return date.toLocaleDateString(locale, { weekday: 'short' });
    },

    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      
      // Use the current app language instead of hard-coded 'en-US'
      const locale = this.getCurrentLocale();
      return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    },
    getCurrentLocale() {
      // Map your app languages to locale codes
      const localeMap = {
        'en': 'en-US',
        'uk': 'uk-UA',
        'pl': 'pl-PL'
      };
      
      // Get current language with proper fallbacks
      let currentLang = 'en'; // Default to English
      
      try {
        // First check if store exists and has language
        if (this.$store && this.$store.state && this.$store.state.language) {
          currentLang = this.$store.state.language;
        }
        // Then try localStorage
        else if (localStorage.getItem('language')) {
          currentLang = localStorage.getItem('language');
        }
        // Finally check i18n locale
        else if (this.$i18n && this.$i18n.locale) {
          currentLang = this.$i18n.locale;
        }
      } catch (error) {
        console.error('Error getting current language:', error);
        // If all else fails, use English
      }
      
      return localeMap[currentLang] || 'en-US';
    },
    formatTime(timeString) {
      if (!timeString) return '—'; // Handle missing time value
      return timeString.substring(0, 5); // Extract HH:MM from HH:MM:SS
    },
    toggleDropdown(event) {
      const dropdown = event.currentTarget.nextElementSibling;
      dropdown.classList.toggle('show');
      
      // Close when clicking outside
      document.addEventListener('click', function closeDropdown(e) {
        if (!dropdown.contains(e.target) && e.target !== event.currentTarget) {
          dropdown.classList.remove('show');
          document.removeEventListener('click', closeDropdown);
        }
      });
    },
    toggleView() {
      this.viewAsTeacher = !this.viewAsTeacher;
      if (this.viewAsTeacher) {
        this.fetchTeachers();
      }
    },
    async fetchTeachers() {
      const name = this.teacherName;
      
      if (name.length >= 2) { // Reduced to 2 characters for better usability
        try {
          const response = await axiosInstance.get('/admin/teachers', {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            params: {
              name // Using name as a query parameter
            }
          });
          
          // Process the response to format teacher data
          const teachers = response.data;
          
          // Map the teachers to a format used in the suggestions
          this.teacherSuggestions = teachers.map(teacher => ({
            id: teacher.id,
            name: `${teacher.first_name} ${teacher.last_name}`,
            // Create a search-friendly version of the name with all variations
            searchText: `${teacher.first_name} ${teacher.last_name} ${teacher.nickname || ''}`.toLowerCase()
          }));
          
          // Filter suggestions by the search term
          const searchTerm = name.toLowerCase();
          this.teacherSuggestions = this.teacherSuggestions.filter(teacher => 
            teacher.name.toLowerCase().includes(searchTerm) || 
            teacher.searchText.includes(searchTerm) ||
            teacher.name.toLowerCase().split(' ').some(part => part.startsWith(searchTerm))
          );
          
          // Set error state if no teachers are found
          this.teacherError = this.teacherSuggestions.length === 0;
          
        } catch (error) {
          console.error('Error fetching teachers:', error);
          this.teacherSuggestions = [];
          this.teacherError = true;
        }
      } else {
        this.teacherSuggestions = [];
        this.teacherError = false;
      }
    },
    selectTeacher(teacher) {
      this.selectedTeacher = teacher.id.toString();
      this.teacherName = teacher.name;
      this.teacherSuggestions = [];
      this.teacherError = false;
      // Logic to view the selected teacher's schedule
      console.log('Viewing schedule for teacher ID:', this.selectedTeacher);
    },
    logout() {
      // First clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('userId');
      localStorage.removeItem('refreshToken');

      // Only try to call the logout endpoint if we might have a valid token
      const token = localStorage.getItem('token');
      if (token) {
        // Attempt to call logout API but don't wait for it or care if it fails
        axiosInstance.post('/logout').catch(() => {
          console.log('Logout API call failed, but continuing with client-side logout');
        });
      }
      
      // Always redirect to login page, regardless of API call success
      this.$router.push('/login');
    },
    changeLanguage(language) {
      try {
        // Try to update in store if it exists
        if (this.$store && typeof this.$store.commit === 'function') {
          this.$store.commit('setLanguage', language);
        }
      } catch (error) {
        console.warn('Could not update language in store:', error);
      }
      
      // Always update localStorage as fallback
      localStorage.setItem('language', language);
      
      // Update current language display directly
      this.currentLanguage = language === 'en' ? 'English' : 
                          language === 'uk' ? 'Українська' :
                          language === 'pl' ? 'Polski' : 'English';
      
      // Close dropdown
      this.isLangDropdownOpen = false;
      document.removeEventListener('click', this.closeLangDropdown);
      
      // Reload the page to apply language changes
      window.location.reload();
    },
    checkTokenExpiration() {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!token) {
        console.log('No token found, redirecting to login');
        this.$router.push('/login');
        return;
      }
      
      try {
        // Parse JWT to check expiration
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeLeft = expTime - currentTime;
        
        // If token is expired and no refresh token exists
        if (currentTime > expTime && !refreshToken) {
          console.log('Token expired and no refresh token available');
          this.logout();
        } else if (timeLeft < 60000 && refreshToken) {
          // If token will expire in less than 1 minute and we have a refresh token,
          // proactively refresh to prevent disruption
          console.log('Token about to expire, proactively refreshing');
          this.refreshToken();
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        this.logout();
      }
    },
    async refreshToken() {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) return;
        
        const response = await axiosInstance.post(
          '/refresh-token', 
          { refreshToken }
        );
        
        localStorage.setItem('token', response.data.token);
      } catch (error) {
        console.error('Manual token refresh failed:', error);
        this.logout();
      }
    }
  },
  mounted() {
    // Detect user's language preference from cookies or browser settings
    const savedLang = localStorage.getItem('language') || 'en';
    this.currentLanguage = savedLang === 'en' ? 'English' : 
                          savedLang === 'uk' ? 'Українська' :
                          savedLang === 'pl' ? 'Polski' : 'English';

    if (this.$route.path === '/admin') {
      this.fetchDashboardStats();
    }
    if (this.$route.path === '/admin' && this.role === 'admin' && !this.viewAsTeacher) {
      this.fetchStudentsInDebt();
      this.fetchFinanceData();
    }
  },
  created() {
    // Check token validity immediately when component is created
    this.checkTokenExpiration();
    
    // Set up periodic checking every 5 seconds
    this.tokenCheckInterval = setInterval(() => {
      this.checkTokenExpiration();
    }, 5000);
  },
  beforeUnmount() {
    // Clear the interval when component is destroyed
    if (this.financeChart) {
      this.financeChart.destroy();
      this.financeChart = null;
    }
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }
    if (this.chartRenderTimeout) {
      clearTimeout(this.chartRenderTimeout);
    }
  }
};
</script>
<style>
:root {
  /* Primary Colors */
  --primary-dark: #2c3e50;     /* Dark blue for sidebar */
  --primary-blue: #3498db;     /* Medium blue for actions and highlights */
  --primary-light: #ecf0f1;    /* Light color for text on dark backgrounds */
  
  /* Neutral Colors */
  --neutral-white: #ffffff;    /* For content areas */
  --neutral-light: #f8f9fa;    /* For backgrounds */
  --neutral-gray: #e1e1e1;     /* For borders */
  --neutral-dark: #2c3e50;     /* For text */
  
  /* Status Colors */
  --success: #2ecc71;          /* Green */
  --warning: #f39c12;          /* Orange/amber for warnings */
  --error: #e74c3c;            /* Red */
  --info: #3498db;             /* Blue for information */
}
</style>

<style scoped>
.admin-dashboard {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background-color: var(--neutral-light);
}

/* Sidebar Styles */
.sidebar {
  width: 260px;
  background-color: var(--primary-dark);
  color: var(--neutral-white);
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid #34495e;
  display: flex;
  align-items: center;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary-light);
}

.sidebar-content {
  padding: 15px 0;
  flex-grow: 1;
}

.nav-links {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: #d0d0d0;
  text-decoration: none;
  transition: all 0.2s ease;
  border-left: 4px solid transparent;
  position: relative;
}

.nav-link:hover {
  background-color: #34495e;
  color: var(--primary-light);
  border-left: 4px solid var(--primary-blue);
}

.nav-link.router-link-active {
  background-color: rgba(52, 152, 219, 0.2);
  color: white;
  border-left: 4px solid var(--primary-blue);
  font-weight: 600;
}

.nav-link::after {
  content: "";
  position: absolute;
  right: 15px;
  opacity: 0;
  transition: opacity 0.2s;
}

.nav-link:hover::after {
  opacity: 1;
}

.nav-link i {
  margin-right: 12px;
  width: 20px;
  text-align: center;
}

/* Main Content Styles */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-x: hidden; /* Prevent horizontal scroll */
  width: calc(100% - 260px); /* Account for sidebar width */
}

.top-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 30px;
  background-color: var(--neutral-white);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid var(--neutral-gray);
}

.header-title h2 {
  color: var(--neutral-dark);
  margin: 0;
  font-weight: 600;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-toggle, .btn-logout, .btn-dropdown {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-toggle {
  background-color: var(--primary-blue);
  color: white;
  border: 2px solid transparent;
  font-weight: 600;
  padding: 10px 18px;
}

.btn-toggle:hover {
  background-color: #2980b9;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.btn-logout {
  background-color: #e74c3c;
  color: white;
}

.btn-logout:hover {
  background-color: #c0392b;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.btn-dropdown {
  background-color: white;
  color: var(--neutral-dark);
  border: 1px solid var(--neutral-gray);
  padding: 10px 18px;
}

.btn-dropdown:hover {
  background-color: var(--neutral-light);
}

.btn-dropdown::after {
  content: "▼";
  margin-left: 8px;
  font-size: 0.7em;
}

.btn-toggle i, .btn-logout i, .btn-dropdown i {
  margin-right: 8px;
}

.dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-menu {
  display: none;
  position: absolute;
  right: 0;
  background-color: #fff;
  min-width: 160px;
  box-shadow: 0 8px 16px rgba(0,0,0,0.1);
  z-index: 1000;
  border-radius: 4px;
  padding: 8px 0;
}

.dropdown-menu.show {
  display: block;
}

.dropdown-item {
  padding: 8px 16px;
  text-decoration: none;
  display: flex;
  align-items: center;
  color: #333;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

.dropdown-item i {
  margin-right: 8px;
}

/* Teacher Selector Styles */
.teacher-selector {
  padding: 20px 30px;
  background-color: var(--neutral-white);
  border-bottom: 2px solid var(--neutral-gray);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.input-label {
  font-weight: 500;
  color: var(--neutral-dark);
}

.input-wrapper {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 10px;
  border: 1px solid var(--neutral-gray);
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.2s;
}

.form-input:focus {
  border-color: var(--primary-blue);
  outline: none;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.25);
}

.dropdown-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background-color: var(--neutral-white);
  border: 1px solid var(--neutral-gray);
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.dropdown-suggestions ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dropdown-suggestions li {
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-suggestions li:hover {
  background-color: #f5f7fa;
  color: var(--primary-blue);
}

.error-message {
  color: var(--error);
  margin-top: 5px;
  font-size: 0.9rem;
}

/* Content Area */
.content-area {
  padding: 20px 30px;
  flex: 1;
  overflow-y: auto;
  background-color: var(--neutral-light);
  width: 100%;
}
/* Status indicators */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-active {
  background-color: rgba(46, 204, 113, 0.1);
  color: var(--success);
  border: 1px solid rgba(46, 204, 113, 0.2);
}

.status-pending {
  background-color: rgba(243, 156, 18, 0.1);
  color: var(--warning);
  border: 1px solid rgba(243, 156, 18, 0.2);
}

.status-inactive {
  background-color: rgba(231, 76, 60, 0.1);
  color: var(--error);
  border: 1px solid rgba(231, 76, 60, 0.2);
}

/* Make Bootstrap dropdowns work */
[data-bs-toggle="dropdown"] + .dropdown-menu {
  display: none;
}

[data-bs-toggle="dropdown"] + .dropdown-menu.show {
  display: block;
}

/* Media Query for smaller screens */
@media (max-width: 768px) {
  .admin-dashboard {
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
    height: auto;
  }
  
  .top-header {
    flex-direction: column;
    gap: 10px;
  }
  
  .header-controls {
    width: 100%;
    justify-content: space-between;
  }
  .quick-stats, .teacher-stats {
    grid-template-columns: 1fr; /* Stack cards vertically on mobile */
  }
}
.dashboard-welcome {
    padding: 20px 0;
    width: 100%;
    max-width: 1400px; /* Limit maximum width for very large screens */
    margin: 0 auto; /* Center content on very wide screens */
  }

  .welcome-header {
    margin-bottom: 30px;
    text-align: center;
  }

  .welcome-header h3 {
    font-size: 1.8rem;
    color: var(--neutral-dark);
    margin-bottom: 10px;
  }

  .welcome-header p {
    color: #666;
    font-size: 1.1rem;
  }

  /* Statistics Cards */
  .quick-stats, .teacher-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 20px;
    margin-top: 30px;
    width: 100%;
  }

  .stat-card {
    background-color: var(--neutral-white);
    border-radius: 8px;
    padding: 20px;
    display: flex;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .stat-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  }

  .stat-icon {
    font-size: 2.5rem;
    margin-right: 20px;
    color: var(--primary-blue);
  }

  .stat-content h4 {
    color: var(--neutral-dark);
    margin: 0 0 5px 0;
    font-size: 1.1rem;
    font-weight: 500;
  }

  .stat-value {
    font-size: 1.8rem;
    font-weight: 600;
    margin: 0;
    color: var(--primary-dark);
  }

  .balance-card .stat-value {
    color: var(--success);
  }

  /* Admin Quick Actions */
  .quick-actions {
    margin-top: 40px;
  }

  .quick-actions h3 {
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: var(--neutral-dark);
  }

  .action-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    width: 100%;
  }

  .action-card {
    background-color: var(--neutral-white);
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .action-card:hover {
    background-color: var(--primary-blue);
    color: white;
    transform: translateY(-5px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  }

  .action-icon {
    font-size: 2rem;
    margin-bottom: 10px;
  }

  /* Teacher Schedule Styles */
  .upcoming-schedule {
    margin-top: 40px;
  }

  .upcoming-schedule h3 {
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: var(--neutral-dark);
  }

  .schedule-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .schedule-item {
    display: flex;
    align-items: center;
    background-color: var(--neutral-white);
    border-radius: 8px;
    padding: 15px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }

  .schedule-date {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px;
    border-radius: 8px;
    background-color: var(--primary-blue);
    color: white;
    min-width: 60px;
    margin-right: 15px;
  }

  .schedule-day {
    font-size: 0.8rem;
    text-transform: uppercase;
  }

  .schedule-date-num {
    font-size: 1.2rem;
    font-weight: 600;
  }

  .schedule-details {
    flex-grow: 1;
  }

  .schedule-time {
    font-weight: 600;
    color: var(--neutral-dark);
    font-size: 1.1rem;
  }

  .schedule-group {
    color: #666;
    margin-top: 5px;
  }

  .schedule-actions {
    display: flex;
    gap: 10px;
  }

  .btn-schedule-action {
    border: none;
    background-color: var(--neutral-light);
    color: var(--primary-blue);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-schedule-action:hover {
    background-color: var(--primary-blue);
    color: white;
  }

  .no-lessons {
    padding: 30px;
    text-align: center;
    background-color: var(--neutral-white);
    border-radius: 8px;
    color: #666;
  }
  /* Finance Analytics Styles */
  .finance-analytics {
  margin-top: 40px;
}

.analytics-section {
  background-color: var(--neutral-white);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.analytics-section h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.5rem;
  color: var(--neutral-dark);
  border-bottom: 1px solid var(--neutral-gray);
  padding-bottom: 10px;
}

/* Students in Debt */
.debt-list {
  max-height: 300px;
  overflow-y: auto;
}

.debt-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid var(--neutral-gray);
}

.debt-item:last-child {
  border-bottom: none;
}

.student-info {
  display: flex;
  align-items: baseline;
  gap: 15px;
}

.student-name {
  font-weight: 500;
  font-size: 1.1rem;
}

.student-balance {
  font-weight: 600;
}

.student-balance.negative {
  color: var(--error);
}

.debt-actions {
  display: flex;
  gap: 10px;
}

.btn-action {
  padding: 6px 12px;
  border-radius: 4px;
  border: none;
  background-color: var(--primary-blue);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.btn-action:hover {
  background-color: #2980b9;
  transform: translateY(-2px);
}

.btn-action.secondary {
  background-color: var(--neutral-light);
  color: var(--neutral-dark);
  border: 1px solid var(--neutral-gray);
}

.btn-action.secondary:hover {
  background-color: var(--neutral-gray);
}

.empty-state {
  text-align: center;
  padding: 30px 0;
  color: var(--success);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 10px;
}

.loading-indicator {
  text-align: center;
  padding: 20px;
  color: var(--neutral-dark);
}

/* Finance Graph */
.graph-controls {
  margin-bottom: 20px;
}

.date-range-selector {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.range-button {
  padding: 6px 12px;
  border-radius: 4px;
  background-color: var(--neutral-light);
  border: 1px solid var(--neutral-gray);
  cursor: pointer;
  transition: all 0.2s;
}

.range-button.active {
  background-color: var(--primary-blue);
  color: white;
  border-color: var(--primary-blue);
}

.graph-container {
  height: 300px;
  position: relative;
}

.graph-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--neutral-gray);
}

.summary-item {
  text-align: center;
  padding: 10px;
  border-radius: 4px;
  background-color: var(--neutral-light);
}

.summary-label {
  font-size: 0.9rem;
  margin-bottom: 5px;
  color: var(--neutral-dark);
}

.summary-value {
  font-size: 1.4rem;
  font-weight: 600;
}

.summary-item.income .summary-value {
  color: var(--success);
}

.summary-item.expenses .summary-value {
  color: var(--warning);
}

.summary-item.profit .summary-value {
  color: var(--primary-blue);
}

.summary-item.profit.negative .summary-value {
  color: var(--error);
}

.profit .summary-value {
  color: #3498db;
  font-weight: bold;
}

.profit .summary-value.negative {
  color: #e74c3c;
}

.chart-wrapper {
  height: 300px;
  width: 100%;
  position: relative;
}
canvas#financeChart {
  width: 100% !important;
  height: 300px !important;
  display: block;
  background-color: white;
  border: 1px solid #eee;
}
.loading-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: var(--neutral-dark);
  font-size: 1.1rem;
}

.loading-chart i {
  font-size: 2rem;
  margin-bottom: 15px;
  color: var(--primary-blue);
}
.period-navigation {
  display: flex;
  align-items: center;
  margin-top: 15px;
  gap: 10px;
}

.nav-button {
  padding: 6px 12px;
  border-radius: 4px;
  background-color: var(--neutral-light);
  border: 1px solid var(--neutral-gray);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.nav-button:hover:not(:disabled) {
  background-color: var(--primary-blue);
  color: white;
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.current-period {
  font-weight: 500;
  padding: 0 10px;
}

.today-button {
  padding: 6px 12px;
  border-radius: 4px;
  background-color: var(--success);
  color: white;
  border: none;
  cursor: pointer;
  margin-left: auto;
}

.today-button:hover:not(:disabled) {
  background-color: #27ae60;
}

.today-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>